import React, { useState, useContext } from "react"
import {
	Typography,
	Button,
	message,
	Space,
	Table,
	DatePicker,
	Card,
	Row,
	Col,
	Spin,
	Select,
	Checkbox,
	Divider,
} from "antd"
import { PlayCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, SettingOutlined } from "@ant-design/icons"
import apiConstants from "../../api"
import axios from "axios"
import moment from "moment"
import "./style.css"
import { GlobalStoreContext } from "../../Store"

const { Title, Text } = Typography
const { Option } = Select

// Configure available APIs
const AVAILABLE_APIS = [
	{
		id: "alldiscoveryMode",
		name: "All Discovery Mode",
		endpoint: "/RunUtilities/v0.1/alldiscoveryMode",
		method: "GET",
		requiresDateRange: true,
		description: "Run discovery mode for all meta not found files",
	},
	{
		id: "alldiscoveryModev2",
		name: "All Discovery Mode v2",
		endpoint: "/RunUtilities/v0.2/discoveryMode",
		method: "GET",
		requiresDateRange: true,
		description: "Run discovery mode v2 for all meta not found files",
	},
	{
		id: "alldiscoveryModev3",
		name: "All Discovery Mode v3",
		endpoint: "/RunUtilities/v0.3/discoveryMode",
		method: "GET",
		requiresDateRange: true,
		description: "Run discovery mode v3 for all meta not found files",
	},
	{
		id: "remapfiles",
		name: "Remap Files",
		endpoint: "/RunUtilities/v0.1/remap-files",
		method: "GET",
		requiresDateRange: true,
		description: "Remap files for all unidentified files",
	},
	{
		id: "onlyextract",
		name: "Only Extract",
		endpoint: "/RunUtilities/v0.1/onlyExtract",
		method: "GET",
		requiresDateRange: true,
		description: "Only extract files",
	},
	{
		id: "pythonscriptconvertable",
		name: "Convert the [PYTHON_SCRIPT_CONVERTABLE] remark files",
		endpoint: "/Converters/v0.1/convert/python-script-convertable-to-xlsx",
		method: "GET",
		requiresDateRange: true,
		description: "Convert the [PYTHON_SCRIPT_CONVERTABLE] remark files",
	},
	{
		id: "pythonscriptdelimiterconvertable",
		name: "Convert the [PYTHON_SCRIPT_DELIMITER_CONVERTABLE] remark files",
		endpoint: "/Converters/v0.1/convert/python-script-delimiter-convertable-to-xlsx",
		method: "GET",
		requiresDateRange: true,
		description: "Convert the [PYTHON_SCRIPT_DELIMITER_CONVERTABLE] remark files",
	},
	{
		id: "pdfmlmodelconvertable",
		name: "Convert the [PDF_ML_MODEL_CONVERTABLE] remark files",
		endpoint: "/Converters/v0.1/convert/pdf-ml-model-convertable",
		method: "GET",
		requiresDateRange: true,
		description: "Convert the [PDF_ML_MODEL_CONVERTABLE] remark files",
	},
	{
		id: "xlsxfillmissingconvertable",
		name: "Convert the [XLSX_FILL_MISSING_CONVERTABLE] remark files",
		endpoint: "/Converters/v0.1/convert/xlsx-fill-missing-convertable",
		method: "GET",
		requiresDateRange: true,
		description: "Convert the [XLSX_FILL_MISSING_CONVERTABLE] remark files",
	},
	{
		id: "xlstoxlsxconvertable",
		name: "Convert the [XLS_TO_XLSX_CONVERTABLE] remark files",
		endpoint: "/Converters/v0.1/convert/python-script-convertable-xls-to-xlsx",
		method: "GET",
		requiresDateRange: true,
		description: "Convert the [XLS_TO_XLSX_CONVERTABLE] remark files",
	},
]

const AllClientApiRun = () => {
	// Get global context functions and state
	const { authToken } = useContext(GlobalStoreContext)

	// Helper function to truncate long API names for button text
	const getButtonText = (apiName, isRunning = false) => {
		if (!apiName) return "API"

		const prefix = isRunning ? "Running " : "Run "
		const suffix = isRunning ? "..." : ""
		const maxLength = 35 // Adjust based on button width

		if (apiName.length <= maxLength - prefix.length - suffix.length) {
			return `${prefix}${apiName}${suffix}`
		}

		const truncatedName = apiName.substring(0, maxLength - prefix.length - suffix.length - 3) + "..."
		return `${prefix}${truncatedName}${suffix}`
	}

	// State variables
	const [startDate, setStartDate] = useState(null)
	const [endDate, setEndDate] = useState(null)
	const [apiRunning, setApiRunning] = useState(false)
	const [apiResults, setApiResults] = useState([])
	const [selectedApi, setSelectedApi] = useState(AVAILABLE_APIS[0])
	const [selectedClients, setSelectedClients] = useState([])

	// Handle date selection with start of day default
	const handleStartDateChange = (date) => {
		if (date) {
			// Clone the date and set to start of day
			const startOfDay = date.clone().startOf("day")
			setStartDate(startOfDay)
		} else {
			setStartDate(null)
		}
	}

	const handleEndDateChange = (date) => {
		if (date) {
			// Clone the date and set to start of day
			const startOfDay = date.clone().startOf("day")
			setEndDate(startOfDay)
		} else {
			setEndDate(null)
		}
	}

	// Handle client selection
	const handleClientSelection = (clientName, checked) => {
		if (checked) {
			setSelectedClients((prev) => [...prev, clientName])
		} else {
			setSelectedClients((prev) => prev.filter((name) => name !== clientName))
		}
	}

	// Handle select all clients
	const handleSelectAllClients = (checked) => {
		if (checked && apiConstants.SSE_CLIENTS) {
			setSelectedClients(apiConstants.SSE_CLIENTS.map((client) => client.name))
		} else {
			setSelectedClients([])
		}
	}

	// Handle API selection
	const handleApiSelection = (apiId) => {
		const api = AVAILABLE_APIS.find((a) => a.id === apiId)
		setSelectedApi(api)
	}

	// Handle API Run for selected clients
	const handleApiRun = async () => {
		// Validate selected clients
		if (selectedClients.length === 0) {
			message.error("Please select at least one client")
			return
		}

		// Validate date range if required
		if (selectedApi.requiresDateRange) {
			if (!startDate || !endDate) {
				message.error("Please select both start date and end date for this API")
				return
			}
			if (startDate.isAfter(endDate)) {
				message.error("Start date cannot be after end date")
				return
			}
		}

		setApiRunning(true)
		setApiResults([])

		// Prepare API parameters
		let apiParams = {}
		if (selectedApi.requiresDateRange) {
			apiParams = {
				startDate: startDate.utc().toISOString(),
				endDate: endDate.utc().toISOString(),
			}
		}

		console.log(`Running ${selectedApi.name} API for clients:`, selectedClients, "with params:", apiParams)

		// Run API for selected clients only
		const results = []
		const clientsToProcess = (apiConstants.SSE_CLIENTS || []).filter((client) =>
			selectedClients.includes(client.name)
		)

		for (const client of clientsToProcess) {
			try {
				const url = `${client.hostUrl}${selectedApi.endpoint}`

				const requestConfig = {
					headers: {
						Authorization: authToken,
					},
				}

				let response
				if (selectedApi.method === "GET") {
					response = await axios.get(url, {
						...requestConfig,
						params: apiParams,
					})
				} else {
					response = await axios.post(url, apiParams, requestConfig)
				}

				results.push({
					client: client.name,
					api: selectedApi.name,
					status: "success",
					response: response.data,
					message: response.data.message || "Success",
				})

				console.log(`Success for client ${client.name}:`, response.data)
			} catch (error) {
				console.error(`Error for client ${client.name}:`, error)
				results.push({
					client: client.name,
					api: selectedApi.name,
					status: "error",
					error: error.response?.data?.message || error.message || "Unknown error",
					message: `Failed: ${error.response?.data?.message || error.message || "Unknown error"}`,
				})
			}
		}

		setApiResults(results)
		setApiRunning(false)

		// Show summary message
		const successCount = results.filter((r) => r.status === "success").length
		const errorCount = results.filter((r) => r.status === "error").length

		if (successCount > 0 && errorCount === 0) {
			message.success(`${selectedApi.name} API completed successfully for ${successCount} clients`)
		} else if (successCount > 0 && errorCount > 0) {
			message.warning(
				`${selectedApi.name} API completed with ${successCount} successes and ${errorCount} failures`
			)
		} else {
			message.error(`${selectedApi.name} API failed for all ${errorCount} clients`)
		}
	}

	return (
		<div className="all-client-api-container">
			<Space direction="vertical" size="large" className="full-width">
				<div className="header-section">
					<Title level={2}>All Client API Run</Title>
					<Text type="secondary">
						Configure and run various APIs for selected clients. Choose an API, select target clients, and
						execute with custom parameters.
					</Text>
				</div>

				{/* API Selection */}
				<Card
					title={
						<>
							<SettingOutlined /> API Configuration
						</>
					}
					size="default"
				>
					<Row gutter={[24, 16]}>
						<Col xs={24} lg={12}>
							<Space direction="vertical" size="small" style={{ width: "100%" }}>
								<Text strong>Select API</Text>
								<Select
									value={selectedApi?.id}
									onChange={handleApiSelection}
									style={{ width: "100%" }}
									size="large"
									placeholder="Choose an API to run"
									showSearch
									optionFilterProp="children"
									filterOption={(input, option) =>
										(
											option?.children?.props?.children?.[0]?.props?.children?.toLowerCase() ?? ""
										).includes(input.toLowerCase()) ||
										(
											option?.children?.props?.children?.[2]?.props?.children?.toLowerCase() ?? ""
										).includes(input.toLowerCase())
									}
									notFoundContent="No APIs found"
								>
									{AVAILABLE_APIS.map((api) => (
										<Option key={api.id} value={api.id}>
											<div>
												<Text strong>{api.name}</Text>
												<br />
												<Text type="secondary" style={{ fontSize: "12px" }}>
													{api.description}
												</Text>
											</div>
										</Option>
									))}
								</Select>
								{selectedApi && (
									<Text type="secondary">
										<strong>Method:</strong> {selectedApi.method} |<strong> Endpoint:</strong>{" "}
										{selectedApi.endpoint} |<strong> Date Range:</strong>{" "}
										{selectedApi.requiresDateRange ? "Required" : "Not Required"}
									</Text>
								)}
							</Space>
						</Col>
						<Col xs={24} lg={12}>
							<Space direction="vertical" size="small" style={{ width: "100%" }}>
								<Text strong>Select Clients</Text>
								<div
									style={{
										border: "1px solid #d9d9d9",
										borderRadius: "6px",
										padding: "8px 12px",
										maxHeight: "200px",
										overflowY: "auto",
									}}
								>
									<Checkbox
										checked={selectedClients.length === (apiConstants.SSE_CLIENTS?.length || 0)}
										indeterminate={
											selectedClients.length > 0 &&
											selectedClients.length < (apiConstants.SSE_CLIENTS?.length || 0)
										}
										onChange={(e) => handleSelectAllClients(e.target.checked)}
										style={{ marginBottom: "8px", fontWeight: "bold" }}
									>
										Select All Clients ({apiConstants.SSE_CLIENTS?.length || 0})
									</Checkbox>
									<Divider style={{ margin: "8px 0" }} />
									{apiConstants.SSE_CLIENTS && apiConstants.SSE_CLIENTS.length > 0 ? (
										apiConstants.SSE_CLIENTS.map((client) => (
											<div key={client.name} style={{ marginBottom: "4px" }}>
												<Checkbox
													checked={selectedClients.includes(client.name)}
													onChange={(e) =>
														handleClientSelection(client.name, e.target.checked)
													}
												>
													{client.name}
												</Checkbox>
											</div>
										))
									) : (
										<Text type="secondary">No clients available</Text>
									)}
								</div>
								<Text type="secondary">
									Selected: {selectedClients.length} / {apiConstants.SSE_CLIENTS?.length || 0} clients
								</Text>
							</Space>
						</Col>
					</Row>
				</Card>

				{/* Date Range Configuration (conditionally shown) */}
				{selectedApi?.requiresDateRange && (
					<Card title="Date Range Configuration" size="default">
						<Row gutter={[24, 16]}>
							<Col xs={24} sm={12} lg={8}>
								<Space direction="vertical" size="small" style={{ width: "100%" }}>
									<Text strong>Start Date (IST)</Text>
									<DatePicker
										showTime={{
											defaultValue: moment("00:00:00", "HH:mm:ss"),
											format: "HH:mm:ss",
										}}
										format="YYYY-MM-DD HH:mm:ss"
										placeholder="Select start date"
										value={startDate}
										onChange={handleStartDateChange}
										style={{ width: "100%" }}
										size="large"
									/>
								</Space>
							</Col>
							<Col xs={24} sm={12} lg={8}>
								<Space direction="vertical" size="small" style={{ width: "100%" }}>
									<Text strong>End Date (IST)</Text>
									<DatePicker
										showTime={{
											defaultValue: moment("00:00:00", "HH:mm:ss"),
											format: "HH:mm:ss",
										}}
										format="YYYY-MM-DD HH:mm:ss"
										placeholder="Select end date"
										value={endDate}
										onChange={handleEndDateChange}
										style={{ width: "100%" }}
										size="large"
									/>
								</Space>
							</Col>
							<Col xs={24} sm={24} lg={8}>
								<Space direction="vertical" size="small" style={{ width: "100%" }}>
									<Text strong>Action</Text>
									<Button
										type="primary"
										icon={apiRunning ? <Spin size="small" /> : <PlayCircleOutlined />}
										onClick={handleApiRun}
										disabled={
											apiRunning ||
											selectedClients.length === 0 ||
											(selectedApi?.requiresDateRange && (!startDate || !endDate))
										}
										loading={apiRunning}
										style={{ width: "100%" }}
										size="large"
									>
										{getButtonText(selectedApi?.name, apiRunning)}
									</Button>
								</Space>
							</Col>
						</Row>
					</Card>
				)}

				{/* Action Button for APIs that don't require date range */}
				{selectedApi && !selectedApi.requiresDateRange && (
					<Card title="Execute API" size="default">
						<Row justify="center">
							<Col xs={24} sm={12} lg={8}>
								<Button
									type="primary"
									icon={apiRunning ? <Spin size="small" /> : <PlayCircleOutlined />}
									onClick={handleApiRun}
									disabled={apiRunning || selectedClients.length === 0}
									loading={apiRunning}
									style={{ width: "100%" }}
									size="large"
								>
									{getButtonText(selectedApi?.name, apiRunning)}
								</Button>
								<div style={{ textAlign: "center", marginTop: "8px" }}>
									<Text type="secondary">
										Will execute for {selectedClients.length} selected client
										{selectedClients.length !== 1 ? "s" : ""}
									</Text>
								</div>
							</Col>
						</Row>
					</Card>
				)}

				{/* API Results Display */}
				{apiResults.length > 0 && (
					<Card title="Execution Results" size="default">
						<Table
							dataSource={apiResults}
							columns={[
								{
									title: "Client",
									dataIndex: "client",
									key: "client",
									width: 120,
									sorter: (a, b) => a.client.localeCompare(b.client),
								},
								{
									title: "API",
									dataIndex: "api",
									key: "api",
									width: 150,
									sorter: (a, b) => a.api.localeCompare(b.api),
								},
								{
									title: "Status",
									key: "status",
									width: 100,
									render: (_, record) => (
										<Text type={record.status === "success" ? "success" : "danger"}>
											{record.status === "success" ? (
												<CheckCircleOutlined />
											) : (
												<CloseCircleOutlined />
											)}{" "}
											{record.status === "success" ? "Success" : "Failed"}
										</Text>
									),
									filters: [
										{ text: "Success", value: "success" },
										{ text: "Failed", value: "error" },
									],
									onFilter: (value, record) => record.status === value,
								},
								{
									title: "Response Message",
									dataIndex: "message",
									key: "message",
									render: (message) => <Text style={{ wordBreak: "break-word" }}>{message}</Text>,
								},
								{
									title: "Timestamp",
									key: "timestamp",
									width: 160,
									render: () => new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }),
								},
							]}
							rowKey="client"
							size="middle"
							pagination={{
								pageSize: 10,
								showSizeChanger: true,
								showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} clients`,
							}}
							scroll={{ x: 600 }}
						/>
					</Card>
				)}

				{/* Summary Statistics */}
				{apiResults.length > 0 && (
					<Card title="Summary" size="small">
						<Row gutter={[16, 16]}>
							<Col xs={12} sm={8} lg={6}>
								<div style={{ textAlign: "center" }}>
									<Title level={3} style={{ color: "#52c41a", margin: 0 }}>
										{apiResults.filter((r) => r.status === "success").length}
									</Title>
									<Text type="success">Successful</Text>
								</div>
							</Col>
							<Col xs={12} sm={8} lg={6}>
								<div style={{ textAlign: "center" }}>
									<Title level={3} style={{ color: "#ff4d4f", margin: 0 }}>
										{apiResults.filter((r) => r.status === "error").length}
									</Title>
									<Text type="danger">Failed</Text>
								</div>
							</Col>
							<Col xs={12} sm={8} lg={6}>
								<div style={{ textAlign: "center" }}>
									<Title level={3} style={{ color: "#1890ff", margin: 0 }}>
										{apiResults.length}
									</Title>
									<Text>Total Clients</Text>
								</div>
							</Col>
							<Col xs={12} sm={8} lg={6}>
								<div style={{ textAlign: "center" }}>
									<Title level={3} style={{ color: "#722ed1", margin: 0 }}>
										{apiResults.length > 0
											? Math.round(
													(apiResults.filter((r) => r.status === "success").length /
														apiResults.length) *
														100
											  )
											: 0}
										%
									</Title>
									<Text>Success Rate</Text>
								</div>
							</Col>
						</Row>
					</Card>
				)}
			</Space>
		</div>
	)
}

export default AllClientApiRun
